import React, { useState, useCallback } from 'react';
import { Upload, CheckCircle, AlertTriangle, FileText, Database, Camera, MapPin, Loader2 } from 'lucide-react';
import { authFetch, API } from '../../utils/api';


export default function AdminUpload() {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/admin/spots-stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, []);

  React.useEffect(() => { loadStats(); }, [loadStats]);

  const handleUpload = async (file) => {
    if (!file || !file.name.endsWith('.csv')) {
      setError('Please upload a .csv file');
      return;
    }
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authFetch(`${API}/api/admin/import-spots`, {
        method: 'POST',
                body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        loadStats();
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Import failed');
      }
    } catch (e) {
      setError('Network error: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
  };

  return (
    <div className="space-y-4" data-testid="admin-upload">
      {/* Current DB Stats */}
      {stats && (
        <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium mb-3">Spots Database</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-lg font-bold text-primary">{stats.total?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Total</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold">{stats.with_sls?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Surfline</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold">{stats.with_cam?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Webcams</p>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <span className="text-[9px] bg-white/5 px-2 py-0.5 rounded-full text-muted-foreground">
              <MapPin className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />{stats.popular || 0} popular
            </span>
            <span className="text-[9px] bg-white/5 px-2 py-0.5 rounded-full text-muted-foreground">
              {stats.spitcast || 0} spitcast
            </span>
            <span className="text-[9px] bg-white/5 px-2 py-0.5 rounded-full text-muted-foreground">
              {stats.community || 0} community
            </span>
          </div>
        </div>
      )}

      {/* Upload Zone */}
      <div
        data-testid="admin-upload-zone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`border-2 border-dashed rounded-2xl p-6 text-center transition-all cursor-pointer ${
          dragOver ? 'border-primary/50 bg-primary/5' : 'border-white/10 hover:border-white/20'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        onClick={() => document.getElementById('csv-file-input')?.click()}
      >
        <input
          id="csv-file-input"
          data-testid="admin-csv-input"
          type="file"
          accept=".csv"
          onChange={onFileSelect}
          className="hidden"
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-xs text-muted-foreground">Importing spots...</p>
            <p className="text-[10px] text-muted-foreground/50">This may take a moment for large files</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-8 h-8 text-muted-foreground/40" />
            <p className="text-xs font-medium">Drop CSV here or click to upload</p>
            <p className="text-[10px] text-muted-foreground/50">Matches by name + proximity, enriches existing, creates new</p>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-2" data-testid="admin-upload-error">
          <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 space-y-3" data-testid="admin-upload-result">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-xs font-semibold text-green-400">Import Complete</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/5 rounded-xl p-2.5 text-center">
              <p className="text-base font-bold">{result.total_rows?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">CSV Rows</p>
            </div>
            <div className="bg-white/5 rounded-xl p-2.5 text-center">
              <p className="text-base font-bold text-green-400">{result.created?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Created</p>
            </div>
            <div className="bg-white/5 rounded-xl p-2.5 text-center">
              <p className="text-base font-bold text-blue-400">{result.enriched?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Enriched</p>
            </div>
            <div className="bg-white/5 rounded-xl p-2.5 text-center">
              <p className="text-base font-bold text-muted-foreground">{result.skipped?.toLocaleString()}</p>
              <p className="text-[9px] text-muted-foreground">Skipped</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
            <Database className="w-3 h-3" />
            <span>Total spots in DB: <strong>{result.total_spots_in_db?.toLocaleString()}</strong></span>
          </div>
        </div>
      )}

      {/* Schema info */}
      <div className="bg-bg-subtle/40 border border-white/5 rounded-xl p-3">
        <p className="text-[10px] font-medium text-muted-foreground mb-1.5 flex items-center gap-1">
          <FileText className="w-3 h-3" /> Expected CSV Schema
        </p>
        <p className="text-[9px] text-muted-foreground/60 leading-relaxed">
          name, lat, lon, region, address, difficulty, wave_type, source, submitted_by, SLS_spot_id, SLS_slug, SLS_subregion, SLS_subregion_id, SLS_has_cam, SLS_conditions, SLS_surf_min_ft, SLS_surf_max_ft, SLS_surf_human
        </p>
      </div>
    </div>
  );
}
